// Libraries
import { all, call, put, takeLatest } from 'redux-saga/effects';

// Dependencies
import serviceAuth, { TROUBLESHOOT_TOKEN } from 'services/auth';
import serviceUser from 'services/user/user';
import { ACCESS_TOKEN } from 'services/utils/accessToken';
import { actions as memberActions } from 'store/modules/member';
import { actions as mentorActions } from 'store/modules/mentor';
import { actions as translationActions } from 'store/modules/translations';
import normalizeUser from 'store/normalizers/userNormalizer';
import { loginMixpanel, signupMixpanel } from 'utils/mixpanel';

// Local Dependencies
import queryString from 'utils/queryString';
import userActions from './actions';
import types from './types';

export function* fetchUser(action = { payload: {} }) {
  const { callback } = action.payload;
  try {
    const response = yield call(serviceUser.getAccount);
    const { user, member, mentor } = yield call(normalizeUser, response.data);
    const isAdmin = mentor && mentor.is_admin;
    if (typeof callback === 'function') callback({ user, member, mentor });
    yield put(
      userActions.userFetchSucceeded({
        userData: {
          is_admin: isAdmin,
          email_verified: user.email_verified,
          ...user
        },
      })
    );
    yield member && put(memberActions.memberFetchSucceeded({ memberData: member }));
    yield mentor && put(mentorActions.mentorFetchSucceeded({ mentorData: mentor }));

    if (member || mentor) {
      const selectedLanguage = (member || mentor).language;
      yield put(translationActions.translationsChange({ selectedLanguage }));
    }
    loginMixpanel(member, mentor, isAdmin);
  } catch (e) {
    yield put(userActions.userFetchFailed({ message: e.message }));
  }
}

export function* userRegister(action) {
  const { username, password, re_password: rePassword, member, mentor, organization, program, callback } =
    action.payload;
  try {
    const user = yield call(serviceAuth.registerAuth, {
      username,
      password,
      re_password: rePassword,
      member,
      mentor,
      organization,
      program,
    });
    yield put(userActions.userRegisterSucceeded({ userData: user.data }));

    if (callback) {
      yield call(callback);
    }
    // Mixpanel event
    signupMixpanel(user.data);
  } catch (e) {
    yield put(userActions.userRegisterFailed({ message: e.response.data }));

    if (callback) {
      // calling callback with error data
      yield call(callback, e.response?.data);
    }
  }
}

// Check if the troubleshoot token is present in the URL and if we're in the troubleshoot mode on a iframe or not
const queryParams = new URLSearchParams(window.location.search);
const troubleshoot = queryParams.get('troubleshoot');

// Get the token from the local storage or the session storage according to the troubleshoot mode
const usedToken = troubleshoot
  ? sessionStorage.getItem(TROUBLESHOOT_TOKEN)
  : localStorage.getItem(ACCESS_TOKEN);

export function* userRestore() {
  const params = queryString.parse(document.location.search);
  const accessToken = usedToken || params.access_token;

  if (accessToken) {
    yield fetchUser();
    yield put(userActions.userIsAccessible());
  }
  yield put(userActions.userIsAccessible());
}

export function* forgotPassword(action) {
  const { email } = action.payload;
  try {
    yield call(serviceAuth.forgotPassword, email);
    yield put(userActions.forgotPasswordSucceeded());
  } catch (e) {
    yield put(
      userActions.forgotPasswordFailed({
        message: (e.response && e.response.data) || e.message,
      })
    );
  }
}

export function* resetPassword(action) {
  try {
    const { user, password } = action.payload;
    yield call(serviceAuth.resetPassword, action.payload);
    yield put(userActions.userLogin({ username: user, password }));
    if (action.callback) {
      yield call(action.callback);
    }
  } catch (e) {
    yield put(
      userActions.userEditFailed({
        message: (e.response && e.response.data) || e.message,
      })
    );
  }
}

export function* setPassword(action) {
  try {
    const { user, re_new_password: reNewPassword, new_password: newPassword, current_password: currentPassword } = action.payload;
    yield call(serviceAuth.setNewPassword, {
      user,
      re_new_password: reNewPassword,
      new_password: newPassword,
      current_password: currentPassword,
    });
    yield put(userActions.userLogin({ username: user, password: newPassword }));
    if (action.callback) {
      yield call(action.callback);
    }
  } catch (e) {
    yield put(
      userActions.userEditFailed({
        message: (e.response && e.response.data) || e.message,
      })
    );
  }
}

export function* userEdit(action) {
  const { uid, token, user, password } = action.payload;
  try {
    yield call(serviceAuth.resetPassword, {
      uid,
      token,
      user,
      password,
    });
    yield put(userActions.userLogin({ username: user, password }));
  } catch (e) {
    yield put(
      userActions.forgotPasswordFailed({
        message: (e.response && e.response.data) || e.message,
      })
    );
  }
}

function* authSaga() {
  yield all([
    takeLatest(types.USER_RESTORE, userRestore),
    takeLatest(types.USER_REGISTER, userRegister),
    takeLatest(types.USER_FORGOT_PASSWORD, forgotPassword),
    takeLatest(types.USER_RESET_PASSWORD, resetPassword),
    takeLatest(types.USER_SET_PASSWORD, setPassword),
    takeLatest(types.USER_EDIT, userEdit),
    takeLatest(types.USER_FETCH_REQUESTED, fetchUser),
  ]);
}

export default authSaga;
